import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

let supabase: any = null;

// Initialize Supabase client with error handling
try {
  if (supabaseUrl && supabaseKey) {
    supabase = createClient(supabaseUrl, supabaseKey);
  }
} catch (err) {
  console.error('Failed to initialize Supabase:', err);
}

interface Order {
  id: string;
  order_id: string;
  order_date: string;
  customer_name: string;
  customer_email?: string;
  products: string;
  total_amount: number;
  commission_amount: number;
  commission_rate: number;
  status: string;
  category?: string;
}

interface Notification {
  id: string;
  order_id: string;
  is_read: boolean;
  created_at: string;
}

const DashboardPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30days');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [latestOrder, setLatestOrder] = useState<Order | null>(null);

  // Check authentication on mount
  useEffect(() => {
    const authStatus = sessionStorage.getItem('dashboard_auth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  // Request browser notification permission
  useEffect(() => {
    if (isAuthenticated && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);

  // Fetch orders and notifications when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
      fetchUnreadNotifications();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [timeRange, isAuthenticated]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple password protection - you can change this password
    const ADMIN_PASSWORD = 'admin2025'; // Change this to your preferred password
    
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('dashboard_auth', 'authenticated');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('dashboard_auth');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchUnreadNotifications = async () => {
    try {
      if (!supabase) return;

      const { data, error: fetchError } = await supabase
        .from('order_notifications')
        .select('*')
        .eq('is_read', false)
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching notifications:', fetchError);
        return;
      }

      const previousCount = unreadCount;
      const newCount = data?.length || 0;
      
      setUnreadCount(newCount);

      // Show browser notification for new orders
      if (newCount > previousCount && data && data.length > 0) {
        const newestNotification = data[0];
        
        // Fetch the order details
        const { data: orderData } = await supabase
          .from('orders')
          .select('*')
          .eq('order_id', newestNotification.order_id)
          .single();

        if (orderData) {
          setLatestOrder(orderData);
          setShowNotificationBanner(true);

          // Show browser notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('🎉 New Order Received!', {
              body: `Order ${orderData.order_id} - $${orderData.total_amount.toFixed(2)}\\nYou earned $${orderData.commission_amount.toFixed(2)}!`,
              icon: '/favicon.ico',
              tag: orderData.order_id,
              requireInteraction: true
            });
          }

          // Auto-hide banner after 10 seconds
          setTimeout(() => {
            setShowNotificationBanner(false);
          }, 10000);
        }
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const markNotificationsAsRead = async () => {
    try {
      if (!supabase) return;

      await supabase
        .from('order_notifications')
        .update({ is_read: true })
        .eq('is_read', false);

      setUnreadCount(0);
      setShowNotificationBanner(false);
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setDataError(null);

      if (!supabase) {
        throw new Error('Database connection not available');
      }

      const now = new Date();
      let startDate = new Date();
      
      switch (timeRange) {
        case '7days':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30days':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90days':
          startDate.setDate(now.getDate() - 90);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      const { data, error: fetchError } = await supabase
        .from('orders')
        .select('*')
        .gte('order_date', startDate.toISOString().split('T')[0])
        .order('order_date', { ascending: false });

      if (fetchError) {
        console.error('Supabase error:', fetchError);
        throw fetchError;
      }

      console.log('Orders fetched:', data);
      setOrders(data || []);
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setDataError(err.message || 'Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          {/* Lock Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent-green rounded-full mb-4 shadow-lg">
              <i className="ri-lock-line text-4xl text-white"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Protected Area
            </h1>
            <p className="text-gray-600">
              Enter your password to access the commission dashboard
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary transition-colors"
                    placeholder="Enter your password"
                    required
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-key-line text-xl text-gray-400"></i>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-error-warning-line text-lg text-red-600"></i>
                  </div>
                  <p className="text-sm text-red-800 flex-1">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-primary to-accent-green text-white py-3 rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-login-box-line mr-2"></i>
                Access Dashboard
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/')}
                className="w-full text-gray-600 hover:text-primary transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Homepage
              </button>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-sm"></i>
              </div>
              This page is password protected for your security
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show dashboard if authenticated
  const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total_amount), 0);
  const totalCommission = orders.reduce((sum, order) => sum + Number(order.commission_amount), 0);
  const completedOrders = orders.filter(o => o.status === 'Completed').length;
  const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

  const getWeeklyData = () => {
    const weekData = Array(7).fill(0).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return {
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        revenue: 0,
        commission: 0,
        date: date.toISOString().split('T')[0]
      };
    });

    orders.forEach(order => {
      const orderDate = order.order_date;
      const dayData = weekData.find(d => d.date === orderDate);
      if (dayData) {
        dayData.revenue += Number(order.total_amount);
        dayData.commission += Number(order.commission_amount);
      }
    });

    return weekData;
  };

  const chartData = getWeeklyData();
  const maxRevenue = Math.max(...chartData.map(d => d.revenue), 1);

  const getCategoryBreakdown = () => {
    const categories: { [key: string]: number } = {};
    
    orders.forEach(order => {
      const category = order.category || 'Other';
      categories[category] = (categories[category] || 0) + Number(order.commission_amount);
    });

    const total = Object.values(categories).reduce((sum, val) => sum + val, 0);
    
    return Object.entries(categories)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: total > 0 ? (amount / total) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  const categoryBreakdown = getCategoryBreakdown();

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Electronics': 'from-blue-500 to-cyan-500',
      'Travel': 'from-orange-500 to-amber-500',
      'Equipment': 'from-green-500 to-emerald-500',
      'Food': 'from-purple-500 to-violet-500',
      'Fashion': 'from-pink-500 to-rose-500',
    };
    return colors[category] || 'from-gray-500 to-slate-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* New Order Notification Banner */}
      {showNotificationBanner && latestOrder && (
        <div className="fixed top-24 right-6 z-50 max-w-md animate-slide-in-right">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-green-500 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                    <i className="ri-notification-badge-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">New Order!</h3>
                    <p className="text-green-100 text-sm">Just received</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNotificationBanner(false)}
                  className="text-white hover:text-green-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Order ID:</span>
                  <span className="text-sm font-bold text-primary">{latestOrder.order_id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Customer:</span>
                  <span className="text-sm font-semibold text-gray-900">{latestOrder.customer_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total:</span>
                  <span className="text-sm font-bold text-gray-900">${latestOrder.total_amount.toFixed(2)}</span>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-600">Your Commission:</span>
                    <span className="text-xl font-bold text-green-600">${latestOrder.commission_amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={markNotificationsAsRead}
                className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Mark as Read
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header with Logout and Notification Badge */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold text-gray-900">
                  Commission Dashboard
                </h1>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Private
                </span>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-bounce">
                    <i className="ri-notification-3-line"></i>
                    {unreadCount} New
                  </span>
                )}
              </div>
              <p className="text-gray-600">
                Track your earnings and performance
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary cursor-pointer"
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
                <option value="year">This Year</option>
              </select>
              {unreadCount > 0 && (
                <button
                  onClick={markNotificationsAsRead}
                  className="relative px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                  title="Mark all as read"
                >
                  <i className="ri-check-double-line"></i>
                  <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                    {unreadCount}
                  </span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                title="Logout"
              >
                <i className="ri-logout-box-line"></i>
              </button>
            </div>
          </div>

          {dataError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{dataError}</p>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                  Live
                </span>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">Total Commission</h3>
              <p className="text-3xl font-bold text-gray-900">
                ${totalCommission.toFixed(2)}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Live
                </span>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">Total Revenue</h3>
              <p className="text-3xl font-bold text-gray-900">
                ${totalRevenue.toFixed(2)}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg">
                  <i className="ri-shopping-bag-line text-2xl text-white"></i>
                </div>
                <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  Live
                </span>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">Total Orders</h3>
              <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                  <i className="ri-bar-chart-box-line text-2xl text-white"></i>
                </div>
                <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                  Live
                </span>
              </div>
              <h3 className="text-sm text-gray-600 mb-1">Avg Order Value</h3>
              <p className="text-3xl font-bold text-gray-900">
                ${averageOrderValue.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Revenue Chart */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                Weekly Performance
              </h3>
              <div className="space-y-4">
                {chartData.map((data, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">
                        {data.day}
                      </span>
                      <span className="text-sm font-bold text-gray-900">
                        ${data.revenue.toFixed(2)}
                      </span>
                    </div>
                    <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-accent-green rounded-full transition-all"
                        style={{ width: `${(data.revenue / maxRevenue) * 100}%` }}
                      ></div>
                      <div className="absolute inset-0 flex items-center px-3">
                        <span className="text-xs font-semibold text-white">
                          Commission: ${data.commission.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Commission Breakdown */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                Commission by Category
              </h3>
              {categoryBreakdown.length > 0 ? (
                <div className="space-y-4">
                  {categoryBreakdown.map((item, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {item.category}
                        </span>
                        <span className="text-sm font-bold text-gray-900">
                          ${item.amount.toFixed(2)}
                        </span>
                      </div>
                      <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getCategoryColor(item.category)} rounded-full`}
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No commission data available
                </div>
              )}
            </div>
          </div>

          {/* Recent Orders Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Recent Orders</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No orders found. Orders will appear here automatically when customers make purchases.
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-primary">
                            {order.order_id}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">{order.order_date}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{order.customer_name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900 line-clamp-1">
                            {order.products}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            ${Number(order.total_amount).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-bold text-green-600">
                            ${Number(order.commission_amount).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              order.status === 'Completed'
                                ? 'bg-green-100 text-green-800'
                                : order.status === 'Processing'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export & Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => alert('Export feature coming soon!')}
              className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              Export Report
            </button>
            <button 
              onClick={() => alert('Email feature coming soon!')}
              className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-primary hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              Email Report
            </button>
            <Link
              to="/products"
              className="px-6 py-3 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-semibold hover:shadow-xl transition-all text-center cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line mr-2"></i>
              Browse Products
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
